import { memo, useMemo } from 'react';

import { Box } from '@zillow/constellation-10';

import { FEE_SOURCE_DATA, FILTER_KEYS_MAP, FREQUENCY_DATA } from '../../constants';

import CostsAndFeesFilterDropdown from './CostsAndFeesFilterDropdown';

import type { GetMultifamilyFeesDictionaryItemsDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  dictionaryItems: GetMultifamilyFeesDictionaryItemsDto;
};

const CostsAndFeesFilters = ({
  dictionaryItems: {
    feeCategory,
    feeRequirementType,
    // Fee level
    feeApplicableLevelType,
    floorplan,
    unit,
  },
}: Props) => {
  const memoedFeeLevelSectionedData = useMemo(() => {
    const feeFloorPlanKeysArray = Object.keys(floorplan);
    const feeUnitKeysArray = Object.keys(unit);

    return {
      BUILDING: {
        isRendered: true,
        heading: feeApplicableLevelType.BUILDING.displayName,
        data: {
          BUILDING: { displayName: `Entire ${feeApplicableLevelType.BUILDING.displayName.toLowerCase()}` },
        },
      },
      FLOOR_PLAN: {
        isRendered: feeFloorPlanKeysArray.length > 0,
        heading: feeApplicableLevelType.FLOOR_PLAN.displayName,
        data: floorplan,
      },
      UNIT: {
        isRendered: feeUnitKeysArray.length > 0,
        heading: feeApplicableLevelType.UNIT.displayName,
        data: unit,
      },
    };
  }, [floorplan, unit, feeApplicableLevelType]);

  return (
    <Box
      data-testid="CostsAndFeesFilters"
      css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '400' }}
    >
      {[
        {
          nonSectionedData: feeCategory,
          keyName: FILTER_KEYS_MAP.CATEGORY,
          ctaText: 'Category',
          nonSectionedDataEscapes: ['ADD_ON'],
        },
        { nonSectionedData: FREQUENCY_DATA, keyName: FILTER_KEYS_MAP.FREQUENCY, ctaText: 'Frequency' },
        { nonSectionedData: feeRequirementType, keyName: FILTER_KEYS_MAP.REQUIREMENT, ctaText: 'Requirement' },
        { sectionedData: memoedFeeLevelSectionedData, keyName: FILTER_KEYS_MAP.FEE_LEVEL, ctaText: 'Fee level' },
        { nonSectionedData: FEE_SOURCE_DATA, keyName: FILTER_KEYS_MAP.FEE_SOURCE, ctaText: 'Fee source' },
      ].map((props) => (
        <CostsAndFeesFilterDropdown key={props.keyName} {...props} />
      ))}
    </Box>
  );
};

export default memo(CostsAndFeesFilters);
